import React, { useState, useEffect } from 'react';
import { Card, NavigationBar, Button, ThemedScreen } from '../../shared/components';
import { storageService } from '../../shared/services';
import { notificationService } from '../../shared/services/notificationService';
import type { EmotionalAlert } from '../../shared/services/notificationService';
import type { DrawingEntry } from '../../shared/services/storageService';
import { AVATARS, AvatarType } from '../../shared/constants/avatars';
import { useAvatar } from '../../shared/hooks/useAvatar';

const ProfileScreen: React.FC<{ userName: string; onNavigate: (screen: string) => void }> = ({ 
  userName, 
  onNavigate 
}) => {
  const [selectedTab, setSelectedTab] = useState<'drawings' | 'stories' | 'thoughts' | 'emotions' | 'all' | 'avatar' | 'profile'>('all');
  const [drawings, setDrawings] = useState<DrawingEntry[]>([]);
  const [stories, setStories] = useState<any[]>([]);
  const [thoughts, setThoughts] = useState<any[]>([]);
  const [emotions, setEmotions] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<EmotionalAlert[]>([]);
  const { selectedAvatar, setSelectedAvatar, selectedAvatarData } = useAvatar();
  const [viewMode, setViewMode] = useState<'tutor' | 'child'>('child');
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [isCreatingPin, setIsCreatingPin] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [childProfile, setChildProfile] = useState<any>(() => {
    const saved = localStorage.getItem('calmi_child_profile');
    return saved ? JSON.parse(saved) : null;
  });
  const [editingChildProfile, setEditingChildProfile] = useState(false);
  const [editedProfile, setEditedProfile] = useState<any>({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem('calmi_notifications') === 'true';
  });
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    return localStorage.getItem('calmi_voice') !== 'false';
  });
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('calmi_language') || 'es';
  });

  useEffect(() => {
    setDrawings(storageService.getDrawings());
    setStories(storageService.getStories());
    setThoughts(storageService.getThoughts());
    setEmotions(storageService.getEmotions());
    setAlerts(notificationService.getAlerts());
  }, []);

  const acknowledgeAlert = (alertId: string) => {
    notificationService.acknowledgeAlert(alertId);
    setAlerts(notificationService.getAlerts());
  };

  const handleAvatarSelect = (avatar: AvatarType) => {
    setSelectedAvatar(avatar);
  };

  const getStoredPin = () => localStorage.getItem('calmi_tutor_pin');

  const handleToggleView = () => {
    const storedPin = getStoredPin();
    if (viewMode === 'child') {
      // Intentar entrar como tutor
      if (storedPin) {
        setPinInput('');
        setPinError('');
        setShowPinModal(true);
      } else {
        setIsCreatingPin(true);
        setNewPin('');
        setConfirmPin('');
        setShowPinModal(true);
      }
    } else {
      // Cerrar sesión de tutor
      setViewMode('child');
      localStorage.setItem('calmi_profile_mode', 'child');
      setSettingsOpen(false);
      setSelectedTab('all');
    }
  };

  const verifyPin = () => {
    const storedPin = getStoredPin();
    if (pinInput === storedPin) {
      setViewMode('tutor');
      localStorage.setItem('calmi_profile_mode', 'tutor');
      setShowPinModal(false);
      setPinInput('');
      setPinError('');
    } else {
      setPinError('PIN incorrecto. Inténtalo de nuevo.');
    }
  };

  const createPin = () => {
    if (newPin.length < 4) {
      setPinError('El PIN debe tener al menos 4 dígitos.');
      return;
    }
    if (newPin !== confirmPin) {
      setPinError('Los PINs no coinciden.');
      return;
    }
    localStorage.setItem('calmi_tutor_pin', newPin);
    setViewMode('tutor');
    localStorage.setItem('calmi_profile_mode', 'tutor');
    setShowPinModal(false);
    setIsCreatingPin(false);
    setNewPin('');
    setConfirmPin('');
    setPinError('');
  };

  const renderDrawings = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">🎨 Dibujos Guardados</h3>
      {drawings.length === 0 ? (
        <p className="text-gray-600 text-center py-8">No hay dibujos guardados aún</p>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {drawings.map((drawing) => (
            <Card key={drawing.id} className="p-4">
              <img 
                src={drawing.imageData} 
                alt="Dibujo" 
                className="w-full h-32 object-cover rounded-lg mb-2"
              />
              <p className="text-sm text-gray-600">
                {drawing.emotion === 'happy' ? '😊 Feliz' : 
                 drawing.emotion === 'sad' ? '😢 Triste' : 
                 drawing.emotion === 'calm' ? '😌 Tranquilo' : '🌈 Libre'}
              </p>
              <p className="text-xs text-gray-500">{new Date(drawing.date).toLocaleDateString()}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderStories = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">📖 Historias Guardadas</h3>
      {stories.length === 0 ? (
        <p className="text-gray-600 text-center py-8">No hay historias guardadas aún</p>
      ) : (
        <div className="space-y-3">
          {stories.map((story) => (
            <Card key={story.id} className="p-4">
              <p className="text-sm text-gray-700 mb-2">{story.story}</p>
              <p className="text-xs text-gray-500">{new Date(story.date).toLocaleDateString()}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderThoughts = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">📝 Pensamientos Guardados</h3>
      {thoughts.length === 0 ? (
        <p className="text-gray-600 text-center py-8">No hay pensamientos guardados aún</p>
      ) : (
        <div className="space-y-3">
          {thoughts.map((thought) => (
            <Card key={thought.id} className="p-4">
              <p className="text-sm text-gray-700 mb-2">{thought.thought}</p>
              <p className="text-xs text-gray-500">{new Date(thought.date).toLocaleDateString()}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderAlerts = () => {
    const unacknowledged = alerts.filter(a => !a.acknowledged);
    return (
      <div className="space-y-4 mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          🔔 Alertas del Tutor {unacknowledged.length > 0 && `(${unacknowledged.length})`}
        </h3>
        {alerts.length === 0 ? (
          <Card className="p-6 text-center">
            <p className="text-gray-600">No hay alertas. El menor está usando Calmi sin patrones preocupantes.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => (
              <Card key={alert.id} className={`p-4 border-l-4 ${
                alert.severity === 'high' ? 'border-red-500 bg-red-50' :
                alert.severity === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                'border-green-500 bg-green-50'
              }`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 mb-1">
                      {notificationService.getSeverityEmoji(alert.severity)} {notificationService.getAlertMessage(alert)}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">{alert.message}</p>
                    <p className="text-xs text-gray-500">{new Date(alert.date).toLocaleString()}</p>
                  </div>
                  {!alert.acknowledged && (
                    <button
                      onClick={() => acknowledgeAlert(alert.id)}
                      className="px-3 py-1 bg-blue-500 text-white text-xs rounded-full hover:bg-blue-600 transition-colors"
                    >
                      Reconocer
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderEmotions = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">😊 Emociones Registradas</h3>
      {emotions.length === 0 ? (
        <p className="text-gray-600 text-center py-8">No hay emociones registradas aún</p>
      ) : (
        <div className="space-y-3">
          {emotions.map((emotion) => (
            <Card key={emotion.id} className="p-4">
              <p className="text-sm text-gray-700 mb-2">
                {emotion.emotion === 'happy' ? '😊 Feliz' : 
                 emotion.emotion === 'sad' ? '😢 Triste' : 
                 emotion.emotion === 'angry' ? '😠 Enojado' : 
                 emotion.emotion === 'anxious' ? '😰 Ansioso' : 
                 emotion.emotion === 'calm' ? '😌 Tranquilo' : emotion.emotion}
              </p>
              <p className="text-xs text-gray-600 mb-1">{emotion.reason}</p>
              <p className="text-xs text-gray-500">{new Date(emotion.date).toLocaleDateString()}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderAvatarSection = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">🎭 Cambiar Mascota</h3>
      <Card className="p-6">
        <div className="text-center mb-6">
          <div className="text-6xl mb-2">{selectedAvatarData.emoji}</div>
          <p className="text-gray-600 font-medium">Mascota actual: {selectedAvatarData.name}</p>
          <p className="text-sm text-gray-500">La mascota cambiará en todo Calmi: menú, chat y diario</p>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {AVATARS.map((avatar) => (
            <button
              key={avatar.id}
              onClick={() => handleAvatarSelect(avatar.id)}
              className={`p-4 rounded-2xl border-2 transition-all ${
                selectedAvatar === avatar.id
                  ? 'border-blue-500 bg-blue-50 scale-105'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:scale-105'
              }`}
            >
              <div className="text-4xl mb-2">{avatar.emoji}</div>
              <p className="text-sm text-gray-700">{avatar.name}</p>
              {selectedAvatar === avatar.id && (
                <div className="text-xs text-blue-600 mt-1">✓ Seleccionado</div>
              )}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">⚙️ Configuración</h3>
      <Card className="p-6">
        <div className="space-y-6">
          {/* Notificaciones */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-800">🔔 Notificaciones</p>
              <p className="text-sm text-gray-600">Recibir recordatorios y alertas</p>
            </div>
            <button
              onClick={() => {
                const newValue = !notificationsEnabled;
                setNotificationsEnabled(newValue);
                localStorage.setItem('calmi_notifications', newValue.toString());
              }}
              className={`w-14 h-8 rounded-full p-1 transition-colors ${
                notificationsEnabled ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                notificationsEnabled ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Voz */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-800">🔊 Voz de Calmi</p>
              <p className="text-sm text-gray-600">Reproducción de voz en el chat</p>
            </div>
            <button
              onClick={() => {
                const newValue = !voiceEnabled;
                setVoiceEnabled(newValue);
                localStorage.setItem('calmi_voice', newValue.toString());
              }}
              className={`w-14 h-8 rounded-full p-1 transition-colors ${
                voiceEnabled ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                voiceEnabled ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Información de la app */}
          <div className="pt-4 border-t border-gray-200">
            <p className="font-medium text-gray-800 mb-2">ℹ️ Información</p>
            <div className="text-sm text-gray-600 space-y-1">
              <p>Versión: 1.0.0</p>
              <p>Calmi - Tu compañero emocional</p>
            </div>
          </div>

          {/* Botón de ayuda */}
          <div className="pt-4 border-t border-gray-200">
            <Button variant="secondary" className="w-full">
              ❓ Ayuda y soporte
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderChildProfileInfo = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-gray-800">👶 Mi Perfil</h3>
        {viewMode === 'tutor' && !editingChildProfile && (
          <Button 
            onClick={() => {
              setEditingChildProfile(true);
              setEditedProfile(childProfile || {});
            }}
            className="text-sm py-2 px-4"
          >
            ✏️ Editar
          </Button>
        )}
      </div>
      
      {editingChildProfile ? (
        <Card className="p-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Nombre</label>
              <input
                type="text"
                value={editedProfile.name || ''}
                onChange={(e) => setEditedProfile({...editedProfile, name: e.target.value})}
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-400"
                placeholder="Tu nombre"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Edad</label>
              <input
                type="number"
                value={editedProfile.age || ''}
                onChange={(e) => setEditedProfile({...editedProfile, age: parseInt(e.target.value) || ''})}
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-400"
                placeholder="Tu edad"
                min="1"
                max="18"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Género</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setEditedProfile({...editedProfile, gender: 'male'})}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    editedProfile.gender === 'male'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-2xl">👦</span>
                  <p className="text-sm font-medium mt-1">Niño</p>
                </button>
                <button
                  type="button"
                  onClick={() => setEditedProfile({...editedProfile, gender: 'female'})}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    editedProfile.gender === 'female'
                      ? 'border-pink-500 bg-pink-50 text-pink-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-2xl">👧</span>
                  <p className="text-sm font-medium mt-1">Niña</p>
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">¿Qué te gusta hacer? (Intereses)</label>
              <input
                type="text"
                value={editedProfile.interests || ''}
                onChange={(e) => setEditedProfile({...editedProfile, interests: e.target.value})}
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-400"
                placeholder="Ej: Jugar fútbol, dibujar, leer"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Tu color favorito</label>
              <input
                type="text"
                value={editedProfile.favoriteColor || ''}
                onChange={(e) => setEditedProfile({...editedProfile, favoriteColor: e.target.value})}
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-400"
                placeholder="Ej: Azul, rojo, verde"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Tu animal favorito</label>
              <input
                type="text"
                value={editedProfile.favoriteAnimal || ''}
                onChange={(e) => setEditedProfile({...editedProfile, favoriteAnimal: e.target.value})}
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-400"
                placeholder="Ej: Perro, gato, conejo"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                onClick={() => {
                  localStorage.setItem('calmi_child_profile', JSON.stringify(editedProfile));
                  setChildProfile(editedProfile);
                  setEditingChildProfile(false);
                }}
                className="flex-1"
              >
                💾 Guardar cambios
              </Button>
              <Button 
                onClick={() => {
                  setEditingChildProfile(false);
                  setEditedProfile({});
                }}
                variant="secondary"
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Nombre</label>
              <p className="text-lg font-semibold text-gray-800">{childProfile?.name || 'No configurado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Edad</label>
              <p className="text-lg font-semibold text-gray-800">{childProfile?.age ? `${childProfile.age} años` : 'No configurado'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Género</label>
              <p className="text-lg font-semibold text-gray-800">{childProfile?.gender === 'female' ? 'Niña' : childProfile?.gender === 'male' ? 'Niño' : 'No configurado'}</p>
            </div>
            {childProfile?.interests && (
              <div>
                <label className="text-sm font-medium text-gray-500">Intereses</label>
                <p className="text-gray-800">{childProfile.interests}</p>
              </div>
            )}
            {childProfile?.favoriteColor && (
              <div>
                <label className="text-sm font-medium text-gray-500">Color favorito</label>
                <p className="text-gray-800">{childProfile.favoriteColor}</p>
              </div>
            )}
            {childProfile?.favoriteAnimal && (
              <div>
                <label className="text-sm font-medium text-gray-500">Animal favorito</label>
                <p className="text-gray-800">{childProfile.favoriteAnimal}</p>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );

  const renderAll = () => (
    <div className="space-y-6">
      {viewMode === 'tutor' && renderAlerts()}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className="p-4 text-center">
          <div className="text-3xl mb-2">🎨</div>
          <div className="text-2xl font-bold text-gray-800">{drawings.length}</div>
          <div className="text-sm text-gray-600">Dibujos</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-3xl mb-2">📖</div>
          <div className="text-2xl font-bold text-gray-800">{stories.length}</div>
          <div className="text-sm text-gray-600">Historias</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-3xl mb-2">📝</div>
          <div className="text-2xl font-bold text-gray-800">{thoughts.length}</div>
          <div className="text-sm text-gray-600">Pensamientos</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-3xl mb-2">😊</div>
          <div className="text-2xl font-bold text-gray-800">{emotions.length}</div>
          <div className="text-sm text-gray-600">Emociones</div>
        </Card>
      </div>
    </div>
  );

  return (
    <ThemedScreen className="p-4 sm:p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">👨‍👩‍👧👶 Mi Familia</h1>
          <p className="text-sm sm:text-base text-gray-600 mb-4">
            Perfiles y configuración de Calmi
          </p>

          {/* Toggle Button */}
          <button
            onClick={handleToggleView}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:shadow-lg transition-all border border-purple-200"
          >
            {viewMode === 'tutor' ? (
              <>
                <span className="text-lg">🚪</span>
                <span className="text-sm font-medium text-gray-700">Cerrar sesión de tutor</span>
              </>
            ) : (
              <>
                <span className="text-lg">👨‍👩‍👧</span>
                <span className="text-sm font-medium text-gray-700">Acceso de tutor</span>
              </>
            )}
          </button>

          {viewMode === 'child' && (
            <p className="text-xs text-gray-500 mt-2">
              Algunas opciones están protegidas. Los adultos pueden usar el botón de arriba.
            </p>
          )}
        </div>

        {/* Perfil del menor resumen (siempre visible arriba) */}
        <Card className="mb-6 p-6 bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200">
          <div className="flex items-center gap-4">
            <div className="text-5xl bg-white rounded-2xl p-3 shadow-sm">
              {selectedAvatarData.emoji}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800">
                {childProfile?.name || 'Perfil del menor'}
              </h2>
              <p className="text-sm text-gray-600">
                {childProfile?.age ? `${childProfile.age} años` : 'Aún no configurado'}
              </p>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => { setSelectedTab('all'); setSettingsOpen(false); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedTab === 'all' && !settingsOpen
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {viewMode === 'tutor' ? 'Todo' : 'Resumen'}
          </button>
          <button
            onClick={() => { setSelectedTab('drawings'); setSettingsOpen(false); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedTab === 'drawings'
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Dibujos
          </button>
          <button
            onClick={() => { setSelectedTab('stories'); setSettingsOpen(false); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedTab === 'stories'
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Historias
          </button>
          {viewMode === 'tutor' && (
            <>
              <button
                onClick={() => { setSelectedTab('thoughts'); setSettingsOpen(false); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedTab === 'thoughts'
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Pensamientos
              </button>
              <button
                onClick={() => { setSelectedTab('emotions'); setSettingsOpen(false); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedTab === 'emotions'
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                Emociones
              </button>
            </>
          )}
          <button
            onClick={() => { setSelectedTab('avatar'); setSettingsOpen(false); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedTab === 'avatar'
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Avatar
          </button>
          <button
            onClick={() => { setSelectedTab('profile'); setSettingsOpen(false); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedTab === 'profile' && !settingsOpen
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Mi Perfil
          </button>
          {viewMode === 'tutor' && (
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                settingsOpen
                  ? 'bg-purple-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              ⚙️ Configuración
            </button>
          )}
        </div>

        {/* Content */}
        <div className="mb-20">
          {!settingsOpen && selectedTab === 'all' && renderAll()}
          {!settingsOpen && selectedTab === 'drawings' && renderDrawings()}
          {!settingsOpen && selectedTab === 'stories' && renderStories()}
          {!settingsOpen && selectedTab === 'thoughts' && renderThoughts()}
          {!settingsOpen && selectedTab === 'emotions' && renderEmotions()}
          {!settingsOpen && selectedTab === 'avatar' && renderAvatarSection()}
          {!settingsOpen && selectedTab === 'profile' && renderChildProfileInfo()}
          {settingsOpen && renderSettings()}
        </div>

        {/* PIN Modal */}
        {showPinModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="p-6 max-w-sm w-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {isCreatingPin ? '🔐 Crear PIN de tutor' : '🔐 Acceso de tutor'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {isCreatingPin 
                  ? 'Crea un PIN para proteger la cuenta del tutor.' 
                  : 'Ingresa el PIN del tutor para continuar.'}
              </p>
              
              {isCreatingPin ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onFocus={(e) => { e.target.value = ''; setNewPin(''); }}
                    placeholder="Nuevo PIN"
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 text-center text-xl tracking-widest"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    name={`new-pin-${Date.now()}`}
                    style={{ WebkitTextSecurity: 'disc' } as any}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onFocus={(e) => { e.target.value = ''; setConfirmPin(''); }}
                    placeholder="Confirmar PIN"
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 text-center text-xl tracking-widest"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    name={`confirm-pin-${Date.now()}`}
                    style={{ WebkitTextSecurity: 'disc' } as any}
                  />
                </div>
              ) : (
                <input
                  type="text"
                  inputMode="numeric"
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onFocus={(e) => { e.target.value = ''; setPinInput(''); }}
                  placeholder="• • • •"
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 text-center text-xl tracking-widest"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-lpignore="true"
                  name={`tutor-pin-${Date.now()}`}
                  style={{ WebkitTextSecurity: 'disc' } as any}
                />
              )}
              
              {pinError && (
                <p className="text-sm text-red-600 mt-2">{pinError}</p>
              )}
              
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => {
                    setShowPinModal(false);
                    setPinError('');
                    setIsCreatingPin(false);
                    setPinInput('');
                    setNewPin('');
                    setConfirmPin('');
                  }}
                  variant="secondary"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={isCreatingPin ? createPin : verifyPin}
                  className="flex-1"
                >
                  {isCreatingPin ? 'Crear' : 'Entrar'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Bottom Navigation */}
        <NavigationBar activeItem="profile" onNavigate={onNavigate} />
      </div>
    </ThemedScreen>
  );
};

export default ProfileScreen;
